/**
 * @krisspy-file
 * @type page
 * @name "AvailabilityCalendar"
 * @title "Availability Outlook"
 * @description "Free rental stock per product and size per day, to spot demand peaks"
 * @routes ["/availability"]
 * @layout "DashboardLayout"
 * @flowName "availability-outlook"
 * @design "reference"
 */

import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Filter,
  Package,
  MapPin,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  CalendarClock,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useUrlFilters } from '../lib/useUrlFilters';
import { useToast } from '../contexts/ToastContext';
import { getLocations } from '../services/locations.service';
import { getProducts } from '../services/products.service';
import { checkAvailability } from '../services/bookings.service';
import {
  getAvailabilityOutlook,
  outlookCellState,
  type OutlookRow,
  type OutlookState,
} from '../services/availabilityOutlook.service';
import { ymdInZoneToUtcStartIso, ymdInZoneToUtcEndIso, utcIsoToZonedYmd } from '../lib/timezone';
import LoadingPlaceholder from '../components/LoadingPlaceholder';

// ---- pure date helpers (operate on 'YYYY-MM-DD' calendar strings) ----
function ymdAddDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function ymdDow(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
function ymdDayNum(ymd: string): number {
  return Number(ymd.slice(8, 10));
}

// Sort sizes into a sensible order (XS < S < M < L < XL …), unknowns alpha, sizeless last.
const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
function sizeRank(s: string | null): number {
  if (s == null) return 9999;
  const i = SIZE_ORDER.indexOf(s.trim().toUpperCase());
  return i >= 0 ? i : 500;
}
function sizeCompare(a: string | null, b: string | null): number {
  const ra = sizeRank(a), rb = sizeRank(b);
  if (ra !== rb) return ra - rb;
  return (a || '').localeCompare(b || '');
}

const WEEK_OPTIONS = ['2', '4', '8', '12'];

interface ProductOption {
  id: string;
  name: string;
  inventoryType: string;
  categoryId: string | null;
  categoryName: string | null;
}

interface SizeBucket {
  size: string | null;
  cells: Record<string, OutlookRow>; // keyed by day
}
interface GProduct {
  productTypeId: string;
  productName: string;
  inventoryType: string;
  maxConcurrent: number | null;
  buckets: SizeBucket[];
}

export default function AvailabilityCalendar() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { accessControl } = useAuth();
  const { timezone, locale } = useDateFormatter();
  const { showToast } = useToast();

  const dow = [
    t('availabilityCalendar.sun'), t('availabilityCalendar.mon'), t('availabilityCalendar.tue'),
    t('availabilityCalendar.wed'), t('availabilityCalendar.thu'), t('availabilityCalendar.fri'),
    t('availabilityCalendar.sat'),
  ];

  // Render one layout at a time (matrix on wide screens, stacked strips on
  // narrow) rather than keeping both in the DOM — halves node count on mobile.
  const initialIsMobile = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches,
    []
  );
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Default to a shorter window on phones (fewer day cells to paint).
  const filterDefaults = useMemo(() => ({
    location: '', category: '', product: '', dir: 'future',
    weeks: initialIsMobile ? '2' : '4', offset: '0', unlimited: '0',
  }), [initialIsMobile]);

  const { getFilter, setFilter, setFilters } = useUrlFilters({
    defaults: filterDefaults,
    storageKey: 'availability-outlook-filters',
  });

  const locationId = getFilter('location');
  const categoryId = getFilter('category');
  const productId = getFilter('product');
  const dir = (getFilter('dir') || 'future') as 'future' | 'historic';
  const weeks = Number(getFilter('weeks') || '4');
  const offset = Number(getFilter('offset') || '0');
  const showUnlimited = getFilter('unlimited') === '1';

  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [rows, setRows] = useState<OutlookRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<OutlookRow | null>(null);
  const [engineFree, setEngineFree] = useState<number | null | 'loading'>(null);

  // ---- window (merchant-tz calendar dates) ----
  const todayYmd = useMemo(() => utcIsoToZonedYmd(new Date().toISOString(), timezone), [timezone]);
  const days = Math.min(weeks * 7, 84);
  const { startYmd, endYmd } = useMemo(() => {
    if (dir === 'future') {
      const s = ymdAddDays(todayYmd, offset * days);
      return { startYmd: s, endYmd: ymdAddDays(s, days - 1) };
    }
    const e = ymdAddDays(todayYmd, -1 - offset * days);
    return { startYmd: ymdAddDays(e, -(days - 1)), endYmd: e };
  }, [todayYmd, dir, offset, days]);

  const dayList = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < days; i++) out.push(ymdAddDays(startYmd, i));
    return out;
  }, [startYmd, days]);

  // ---- load locations once, pick a default ----
  useEffect(() => {
    (async () => {
      // Only active locations — a deactivated location takes no bookings, so its
      // availability is operationally moot on this planning view.
      const { data } = await getLocations(accessControl, { active: true });
      const opts = (data || []).map((l: any) => ({ id: l.id, name: l.location_name }));
      setLocations(opts);
      if (!locationId && opts.length > 0) {
        setFilter('location', opts[0].id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessControl]);

  // ---- load product/category options for the selected location ----
  useEffect(() => {
    if (!locationId) return;
    (async () => {
      const { data } = await getProducts(accessControl, { locationId, active: true });
      setProducts(
        (data || []).map((p: any) => ({
          id: p.id,
          name: p.product_name,
          inventoryType: p.inventory_type,
          categoryId: p.category_id ?? null,
          categoryName: p.category_name ?? null,
        }))
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessControl, locationId]);

  // ---- load the outlook grid ----
  useEffect(() => {
    if (!locationId) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await getAvailabilityOutlook({
        locationId,
        startYmd,
        endYmd,
        categoryId: categoryId || undefined,
        productTypeId: productId || undefined,
      });
      if (cancelled) return;
      if (error) {
        showToast(error, 'error');
        setRows([]);
      } else {
        setRows(data || []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, startYmd, endYmd, categoryId, productId]);

  // Close the detail panel when the window/filters change.
  useEffect(() => { setSelected(null); }, [locationId, startYmd, endYmd, categoryId, productId]);

  const categoryOptions = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach((p) => { if (p.categoryId) map.set(p.categoryId, p.categoryName || p.categoryId); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [products]);

  // ---- build the grid: product -> size buckets ----
  const grid = useMemo<GProduct[]>(() => {
    const byProduct = new Map<string, GProduct>();
    const bucketIndex = new Map<string, Map<string, SizeBucket>>();
    for (const r of rows) {
      if (!showUnlimited && !productId && r.inventoryType === 'unlimited') continue;
      let g = byProduct.get(r.productTypeId);
      if (!g) {
        g = {
          productTypeId: r.productTypeId, productName: r.productName,
          inventoryType: r.inventoryType, maxConcurrent: r.maxConcurrent, buckets: [],
        };
        byProduct.set(r.productTypeId, g);
        bucketIndex.set(r.productTypeId, new Map());
      }
      const bi = bucketIndex.get(r.productTypeId)!;
      const key = r.size ?? '__nosize__';
      let b = bi.get(key);
      if (!b) { b = { size: r.size, cells: {} }; bi.set(key, b); g.buckets.push(b); }
      b.cells[r.day] = r;
    }
    const arr = Array.from(byProduct.values());
    arr.forEach((g) => g.buckets.sort((a, b) => sizeCompare(a.size, b.size)));
    arr.sort((a, b) => a.productName.localeCompare(b.productName));
    return arr;
  }, [rows, showUnlimited, productId]);

  // Product-level aggregate for one day: for individual, sum sizes then apply
  // the concurrency cap; for pool/unlimited use the single bucket as-is.
  const aggCell = useCallback((g: GProduct, day: string): OutlookRow | null => {
    if (g.inventoryType !== 'individual') {
      return g.buckets[0]?.cells[day] ?? null;
    }
    let capRaw = 0, booked = 0, blocked = 0, closed = false, has = false;
    for (const b of g.buckets) {
      const c = b.cells[day];
      if (!c) continue;
      has = true;
      if (c.isClosed) closed = true;
      if (c.capacity == null) continue;
      capRaw += c.capacity; booked += c.booked; blocked += c.blocked;
    }
    if (!has) return null;
    const effCap = g.maxConcurrent != null ? Math.min(capRaw, g.maxConcurrent) : capRaw;
    const free = Math.max(0, effCap - blocked - booked);
    return {
      day, locationId, productTypeId: g.productTypeId, productName: g.productName,
      categoryId: null, inventoryType: 'individual', productType: null, size: null,
      capacity: effCap, booked, blocked, free, maxConcurrent: g.maxConcurrent, isClosed: closed,
    };
  }, [locationId]);

  // ---- summary + variant-level KPIs (exclude unlimited) ----
  const summary = useMemo(() => {
    const perDayFree: Record<string, number> = {};
    const perDayHot: Record<string, number> = {};
    let fullVariantDays = 0;
    const zeroVariants = new Set<string>();
    let scarceVariants = 0;
    for (const g of grid) {
      if (g.inventoryType === 'unlimited') continue;
      for (const day of dayList) {
        const agg = aggCell(g, day);
        if (agg && typeof agg.free === 'number') perDayFree[day] = (perDayFree[day] || 0) + agg.free;
      }
      for (const b of g.buckets) {
        scarceVariants++;
        const vkey = `${g.productTypeId}|${b.size ?? ''}`;
        for (const day of dayList) {
          const cell = b.cells[day];
          if (!cell) continue;
          const st = outlookCellState(cell);
          if (st === 'full') { fullVariantDays++; zeroVariants.add(vkey); perDayHot[day] = (perDayHot[day] || 0) + 1; }
          else if (st === 'tight') perDayHot[day] = (perDayHot[day] || 0) + 0.5;
        }
      }
    }
    let peakDay = ''; let peakScore = -1;
    for (const day of dayList) {
      const s = perDayHot[day] || 0;
      if (s > peakScore) { peakScore = s; peakDay = day; }
    }
    return { perDayFree, perDayHot, fullVariantDays, zeroVariants: zeroVariants.size, scarceVariants, peakDay: peakScore > 0 ? peakDay : '' };
  }, [grid, dayList, aggCell]);

  // ---- drill-down: authoritative availability via the canonical engine ----
  const openDetail = useCallback(async (row: OutlookRow) => {
    setSelected(row);
    if (row.isClosed || row.inventoryType === 'unlimited') { setEngineFree(null); return; }
    setEngineFree('loading');
    const startIso = ymdInZoneToUtcStartIso(row.day, timezone);
    const endIso = ymdInZoneToUtcEndIso(row.day, timezone);
    const { data, error } = await checkAvailability({
      locationId,
      startDate: startIso,
      endDate: endIso,
      productTypeId: row.productTypeId,
      size: row.size || undefined,
    });
    if (error || !data) { setEngineFree(null); return; }
    let free = 0;
    for (const p of data) {
      if (p.product_type_id !== row.productTypeId) continue;
      if (p.inventory_type === 'pool' || p.inventory_type === 'unlimited') free += p.pool_quantity || 0;
      else free += 1;
    }
    setEngineFree(free);
  }, [timezone, locationId]);

  const changePage = (delta: number) => setFilter('offset', String(Math.max(0, offset + delta)));

  const stateLabel = (st: OutlookState): string => ({
    available: t('availabilityCalendar.available'),
    tight: t('availabilityCalendar.tightState'),
    full: t('availabilityCalendar.fullyBooked'),
    maintenance: t('availabilityCalendar.maintenance'),
    closed: t('availabilityCalendar.closed'),
    unlimited: t('availabilityCalendar.unlimited'),
  }[st]);

  const cellBig = (cell: OutlookRow, st: OutlookState): string => {
    if (st === 'closed') return '—';
    if (st === 'unlimited') return '∞';
    if (st === 'maintenance') return t('availabilityCalendar.maintAbbr');
    return String(cell.free ?? 0);
  };

  // Compact label for the narrow mobile chips (no room for "Maint").
  const chipLabel = (cell: OutlookRow, st: OutlookState): string => {
    if (st === 'closed') return '—';
    if (st === 'unlimited') return '∞';
    if (st === 'maintenance') return 'M';
    return String(cell.free ?? 0);
  };

  const sizeName = (size: string | null) =>
    size == null ? t('availabilityCalendar.noSize') : t('availabilityCalendar.sizeRow', { size });

  // Month grouping for the header band + boundary dividers (localized).
  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(locale || undefined, { month: 'short', year: 'numeric' }),
    [locale]
  );
  const monthShortFmt = useMemo(
    () => new Intl.DateTimeFormat(locale || undefined, { month: 'short' }),
    [locale]
  );
  const monthShort = (ymd: string) => {
    const [y, m] = ymd.split('-').map(Number);
    return monthShortFmt.format(new Date(y, m - 1, 1));
  };
  const monthSegments = useMemo(() => {
    const segs: { key: string; len: number; label: string; startDay: string }[] = [];
    for (const ymd of dayList) {
      const key = ymd.slice(0, 7);
      const last = segs[segs.length - 1];
      if (last && last.key === key) { last.len++; continue; }
      const [y, m] = ymd.split('-').map(Number);
      segs.push({ key, len: 1, label: monthFmt.format(new Date(y, m - 1, 1)), startDay: ymd });
    }
    return segs;
  }, [dayList, monthFmt]);
  // Days that start a new month (for the vertical divider), excluding the first column.
  const monthStartDays = useMemo(
    () => new Set(monthSegments.slice(1).map((s) => s.startDay)),
    [monthSegments]
  );

  // Localized "15 Jan – 12 Feb 2026" range label for the toolbar.
  const windowLabel = useMemo(() => {
    const [ys, ms, ds] = startYmd.split('-').map(Number);
    const [ye, me, de] = endYmd.split('-').map(Number);
    const s = new Intl.DateTimeFormat(locale || undefined, { day: 'numeric', month: 'short' }).format(new Date(ys, ms - 1, ds));
    const e = new Intl.DateTimeFormat(locale || undefined, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(ye, me - 1, de));
    return `${s} – ${e}`;
  }, [startYmd, endYmd, locale]);

  // Render a single day cell button (or an empty placeholder) for the matrix.
  const renderMatrixCell = (cell: OutlookRow | null, key: string, day: string) => {
    const mstart = monthStartDays.has(day);
    if (!cell) return <div key={key} className={mstart ? 'ao-mstart' : undefined} style={{ borderBottom: '1px solid var(--border-color,#e4e8ee)', borderRight: '1px solid var(--border-color,#e4e8ee)' }} />;
    const st = outlookCellState(cell);
    return (
      <button key={key} className={`ao-cell st-${st}${mstart ? ' ao-mstart' : ''}`} style={{ borderBottom: '1px solid var(--border-color,#e4e8ee)', borderRight: '1px solid var(--border-color,#e4e8ee)' }} onClick={() => openDetail(cell)} title={`${cell.productName}${cell.size ? ' · ' + cell.size : ''} · ${cell.day}`}>
        <span className="ao-fx" />
        <span className="ao-lbl">{cellBig(cell, st)}</span>
        {st !== 'closed' && st !== 'unlimited' && st !== 'maintenance' && <span className="ao-sm">/{cell.capacity}</span>}
      </button>
    );
  };

  return (
    <div className="ao space-y-2xl animate-fade-in" data-testid="availability-outlook">
      <style>{`
        .ao .ao-scroll { overflow-x: auto; touch-action: pan-x; overscroll-behavior-inline: contain; -webkit-overflow-scrolling: touch; }
        .ao .ao-matrix { display: grid; min-width: max-content; }
        .ao .ao-monthband { position: sticky; top: 0; z-index: 4; background: var(--bg-primary, #fff); height: 24px; display: flex; align-items: center; padding: 0 8px; font-size: 11px; font-weight: 700; letter-spacing: .02em; color: var(--text-secondary, #4a5766); border-bottom: 1px solid var(--border-color, #e4e8ee); white-space: nowrap; overflow: hidden; }
        .ao .ao-monthcorner { position: sticky; top: 0; left: 0; z-index: 6; background: var(--bg-primary, #fff); height: 24px; border-bottom: 1px solid var(--border-color, #e4e8ee); box-shadow: 1px 0 0 var(--border-color, #e4e8ee); }
        .ao .ao-colhead { position: sticky; top: 24px; z-index: 2; background: var(--bg-primary, #fff); text-align: center; padding: 6px 0 7px; border-bottom: 1px solid var(--border-color, #e4e8ee); }
        .ao .ao-namecol { position: sticky; left: 0; z-index: 3; background: var(--bg-primary, #fff); box-shadow: 1px 0 0 var(--border-color, #e4e8ee); }
        .ao .ao-corner { z-index: 5; top: 24px; }
        .ao .ao-mstart { border-left: 2px solid #c3cad4; }
        .ao .ao-name { padding: 0 12px; min-height: 46px; display: flex; flex-direction: column; justify-content: center; gap: 1px; border-bottom: 1px solid var(--border-color, #e4e8ee); overflow: hidden; min-width: 0; }
        .ao .ao-prodname { font-weight: 600; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
        .ao .ao-name-size { padding-left: 26px; min-height: 40px; }
        .ao .ao-name-size .ao-prodname { font-weight: 500; font-size: 12px; color: var(--text-secondary, #4a5766); }
        .ao .ao-meta { font-size: 10.5px; color: #8b96a6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
        .ao .ao-cell { min-height: 46px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; border: 0; background: transparent; cursor: pointer; position: relative; width: 100%; font-family: inherit; }
        .ao .ao-cell.sz { min-height: 40px; }
        .ao .ao-fx { position: absolute; inset: 3px; border-radius: 7px; }
        .ao .ao-lbl { position: relative; z-index: 1; font-weight: 700; font-size: 14px; font-variant-numeric: tabular-nums; }
        .ao .ao-sm { position: relative; z-index: 1; font-size: 9.5px; font-weight: 600; opacity: .7; font-variant-numeric: tabular-nums; }
        .ao .ao-cell:focus-visible { outline: 2px solid #2563eb; outline-offset: -2px; border-radius: 7px; }
        .ao .st-available .ao-fx { background: #fbe4c4; } .ao .st-available .ao-lbl, .ao .st-available .ao-sm { color: #6f5426; }
        .ao .st-tight .ao-fx { background: #e8801e; } .ao .st-tight .ao-lbl, .ao .st-tight .ao-sm { color: #2f2005; }
        .ao .st-full .ao-fx { background: #b62317; } .ao .st-full .ao-lbl, .ao .st-full .ao-sm { color: #fff; }
        .ao .st-maintenance .ao-fx { background: #c7cdd4; background-image: repeating-linear-gradient(45deg, transparent 0 4px, rgba(20,28,38,.16) 4px 6px); } .ao .st-maintenance .ao-lbl { color: #444e5b; font-size: 11px; }
        .ao .st-closed .ao-fx { background: var(--bg-secondary, #f7f9fc); border: 1px dashed #cdd4de; background-image: repeating-linear-gradient(45deg, transparent 0 5px, rgba(20,28,38,.09) 5px 6px); } .ao .st-closed .ao-lbl { color: #8b96a6; font-weight: 600; }
        .ao .st-unlimited .ao-lbl { color: #8b96a6; font-size: 16px; }
        .ao .ao-sw { width: 15px; height: 15px; border-radius: 4px; display: inline-block; }
        .ao .ao-sumcell { min-height: 30px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; background: var(--bg-secondary, #f7f9fc); }
        .ao .ao-stack { display: none; }
        .ao .ao-chiprow { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 4px; touch-action: pan-x; overscroll-behavior-inline: contain; -webkit-overflow-scrolling: touch; }
        .ao .ao-chip { flex: none; width: 50px; border-radius: 9px; padding: 6px 0 7px; display: flex; flex-direction: column; align-items: center; gap: 1px; cursor: pointer; border: 0; font-family: inherit; }
        .ao .ao-chip .cd { font-size: 9.5px; text-transform: uppercase; opacity: .75; font-weight: 600; }
        .ao .ao-chip .cn { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }
        .ao .ao-chip.st-available { background: #fbe4c4; color: #6f5426; }
        .ao .ao-chip.st-tight { background: #e8801e; color: #2f2005; }
        .ao .ao-chip.st-full { background: #b62317; color: #fff; }
        .ao .ao-chip.st-maintenance { background: #c7cdd4; color: #444e5b; }
        .ao .ao-chip.st-closed { background: #f7f9fc; color: #8b96a6; border: 1px dashed #cdd4de; }
        .ao .ao-chip.st-unlimited { background: #f7f9fc; color: #8b96a6; }
        .ao .ao-sizelabel { font-size: 11px; color: var(--text-secondary,#4a5766); margin: 6px 0 4px; font-weight: 600; }
        .ao .ao-monthsep { flex: none; display: flex; align-items: center; padding: 0 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: var(--text-secondary,#4a5766); border-left: 2px solid #c3cad4; }
        @media (max-width: 760px) { .ao .ao-scroll { display: none; } .ao .ao-stack { display: block; } }
      `}</style>

      {/* Header */}
      <div className="page-header" data-testid="availability-outlook-header">
        <div className="page-header-title">
          <h1 className="text-3xl font-bold text-primary mb-sm">{t('availabilityCalendar.title')}</h1>
          <p className="text-secondary">{t('availabilityCalendar.outlookSubtitle')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-md mb-lg">
          <Filter className="w-5 h-5 text-secondary" />
          <h3 className="font-semibold text-primary">{t('availabilityCalendar.filters')}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-lg mb-lg">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('availabilityCalendar.selectLocation')}</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select value={locationId} onChange={(e) => setFilters({ location: e.target.value, product: '', category: '', offset: '0' })} className="input pl-10 w-full appearance-none">
                {locations.length === 0 && <option value="">—</option>}
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('availabilityCalendar.selectCategory')}</label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select value={categoryId} onChange={(e) => setFilters({ category: e.target.value, offset: '0' })} className="input pl-10 w-full appearance-none">
                <option value="">{t('availabilityCalendar.allCategories')}</option>
                {categoryOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">{t('availabilityCalendar.selectProduct')}</label>
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select value={productId} onChange={(e) => setFilters({ product: e.target.value, offset: '0' })} className="input pl-10 w-full appearance-none">
                <option value="">{t('availabilityCalendar.allProducts')}</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-md">
          <div className="flex bg-secondary rounded-lg p-1 gap-1">
            <button onClick={() => setFilters({ dir: 'future', offset: '0' })} className={`btn text-sm ${dir === 'future' ? 'btn-primary' : 'btn-ghost'}`}>{t('availabilityCalendar.upcoming')}</button>
            <button onClick={() => setFilters({ dir: 'historic', offset: '0' })} className={`btn text-sm ${dir === 'historic' ? 'btn-primary' : 'btn-ghost'}`}>{t('availabilityCalendar.historic')}</button>
          </div>
          <div className="flex items-center gap-xs">
            <label className="text-sm text-secondary">{t('availabilityCalendar.window')}</label>
            <select value={String(weeks)} onChange={(e) => setFilters({ weeks: e.target.value, offset: '0' })} className="input appearance-none">
              {WEEK_OPTIONS.map((w) => <option key={w} value={w}>{t('availabilityCalendar.nWeeks', { count: Number(w) })}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-xs text-sm text-secondary cursor-pointer">
            <input type="checkbox" checked={showUnlimited} onChange={(e) => setFilter('unlimited', e.target.checked ? '1' : '0')} />
            {t('availabilityCalendar.showUnlimited')}
          </label>
          <div className="flex items-center gap-md ml-auto">
            <button onClick={() => changePage(-1)} disabled={offset === 0} className="btn btn-ghost" aria-label={t('availabilityCalendar.prev')}><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-semibold text-primary min-w-[190px] text-center">{windowLabel}</span>
            <button onClick={() => changePage(1)} className="btn btn-ghost" aria-label={t('availabilityCalendar.next')}><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
        <div className="card">
          <div className="flex items-start justify-between mb-md">
            <div className="w-10 h-10 rounded-md bg-danger/10 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-danger" /></div>
          </div>
          <p className="text-secondary text-sm mb-xs">{t('availabilityCalendar.fullVariantDays')}</p>
          <p className="text-2xl font-bold text-primary">{summary.fullVariantDays}</p>
        </div>
        <div className="card">
          <div className="flex items-start justify-between mb-md">
            <div className="w-10 h-10 rounded-md bg-warning/10 flex items-center justify-center"><Package className="w-5 h-5 text-warning" /></div>
          </div>
          <p className="text-secondary text-sm mb-xs">{t('availabilityCalendar.variantsHittingZero')}</p>
          <p className="text-2xl font-bold text-primary">{summary.zeroVariants}<span className="text-base text-muted font-semibold"> / {summary.scarceVariants}</span></p>
        </div>
        <div className="card">
          <div className="flex items-start justify-between mb-md">
            <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center"><CalendarClock className="w-5 h-5 text-blue" /></div>
          </div>
          <p className="text-secondary text-sm mb-xs">{t('availabilityCalendar.peakDay')}</p>
          <p className="text-2xl font-bold text-primary">{summary.peakDay ? `${dow[ymdDow(summary.peakDay)]} ${ymdDayNum(summary.peakDay)}` : '—'}</p>
        </div>
      </div>

      {/* Grid panel */}
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-md mb-lg">
          <h2 className="text-xl font-bold text-primary">{t('availabilityCalendar.freeByDay')}</h2>
          <div className="flex flex-wrap items-center gap-md text-sm text-secondary">
            <span className="flex items-center gap-xs"><span className="ao-sw" style={{ background: '#fbe4c4' }} />{t('availabilityCalendar.freeStock')}</span>
            <span className="flex items-center gap-xs"><span className="ao-sw" style={{ background: '#e8801e' }} />{t('availabilityCalendar.tightState')}</span>
            <span className="flex items-center gap-xs"><span className="ao-sw" style={{ background: '#b62317' }} />{t('availabilityCalendar.fullyBooked')}</span>
            <span className="flex items-center gap-xs"><span className="ao-sw" style={{ background: '#c7cdd4', backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 3px, rgba(20,28,38,.16) 3px 5px)' }} />{t('availabilityCalendar.maintenance')}</span>
            <span className="flex items-center gap-xs"><span className="ao-sw" style={{ background: '#f7f9fc', border: '1px dashed #cdd4de' }} />{t('availabilityCalendar.closed')}</span>
          </div>
        </div>

        {loading ? (
          <LoadingPlaceholder />
        ) : grid.length === 0 ? (
          <div className="text-center py-2xl text-secondary text-sm">{t('availabilityCalendar.noProducts')}</div>
        ) : (
          <>
            {/* Desktop matrix (rendered only on wider screens) */}
            {!isMobile && (
            <div className="ao-scroll">
              <div className="ao-matrix" style={{ gridTemplateColumns: `minmax(170px, 210px) repeat(${days}, minmax(44px, 1fr))` }}>
                {/* Month band */}
                <div className="ao-namecol ao-monthcorner" />
                {monthSegments.map((seg, i) => (
                  <div key={seg.key} className={`ao-monthband${i > 0 ? ' ao-mstart' : ''}`} style={{ gridColumn: `span ${seg.len}` }}>{seg.label}</div>
                ))}
                {/* Weekday / day-number header */}
                <div className="ao-namecol ao-corner ao-colhead" style={{ display: 'flex', alignItems: 'flex-end', padding: '6px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  <span className="text-muted">{t('availabilityCalendar.product')}</span>
                </div>
                {dayList.map((day) => {
                  const wknd = ymdDow(day) === 0 || ymdDow(day) === 6;
                  const isToday = day === todayYmd;
                  return (
                    <div key={day} className={`ao-colhead${monthStartDays.has(day) ? ' ao-mstart' : ''}`} style={wknd ? { background: 'var(--bg-secondary, #f7f9fc)' } : undefined}>
                      <div className="text-xs" style={{ color: isToday ? '#2563eb' : '#8b96a6' }}>{dow[ymdDow(day)]}</div>
                      <div className="text-sm font-semibold" style={{ color: isToday ? '#2563eb' : 'inherit', fontVariantNumeric: 'tabular-nums' }}>{ymdDayNum(day)}</div>
                    </div>
                  );
                })}

                {grid.map((g) => {
                  const multi = g.buckets.length > 1;
                  const sample = g.buckets[0];
                  const sampleSize = !multi && sample ? sample.size : null;
                  return (
                    <Fragment key={g.productTypeId}>
                      {/* product row (aggregate, cap-applied) */}
                      <div className="ao-namecol ao-name">
                        <span className="ao-prodname" title={g.productName}>{g.productName}</span>
                        <span className="ao-meta">
                          {g.inventoryType === 'unlimited' ? t('availabilityCalendar.unlimitedCap')
                            : g.inventoryType === 'pool' ? t('availabilityCalendar.pool')
                            : multi ? t('availabilityCalendar.sizesCount', { count: g.buckets.length })
                            : sampleSize ? sizeName(sampleSize) : t('availabilityCalendar.individual')}
                          {g.maxConcurrent != null ? ` · ${t('availabilityCalendar.maxNote', { count: g.maxConcurrent })}` : ''}
                        </span>
                      </div>
                      {dayList.map((day) => renderMatrixCell(aggCell(g, day), `${g.productTypeId}-agg-${day}`, day))}

                      {/* size sub-rows */}
                      {multi && g.buckets.map((b) => (
                        <Fragment key={`${g.productTypeId}-${b.size ?? 'none'}`}>
                          <div className="ao-namecol ao-name ao-name-size">
                            <span className="ao-prodname">{sizeName(b.size)}</span>
                          </div>
                          {dayList.map((day) => renderMatrixCell(b.cells[day] ?? null, `${g.productTypeId}-${b.size ?? 'none'}-${day}`, day))}
                        </Fragment>
                      ))}
                    </Fragment>
                  );
                })}

                {/* Summary row */}
                <div className="ao-namecol ao-name" style={{ minHeight: 30, justifyContent: 'center' }}>
                  <span className="ao-meta" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>{t('availabilityCalendar.freeAllProducts')}</span>
                </div>
                {dayList.map((day) => {
                  const total = summary.perDayFree[day] ?? 0;
                  const hot = (summary.perDayHot[day] ?? 0) >= 3;
                  return <div key={`sum-${day}`} className={`ao-sumcell${monthStartDays.has(day) ? ' ao-mstart' : ''}`} style={{ color: hot ? '#b62317' : 'var(--text-secondary, #4a5766)' }}>{total}</div>;
                })}
              </div>
            </div>
            )}

            {/* Mobile stacked strips (rendered only on narrow screens) */}
            {isMobile && (
            <div className="ao-stack">
              {grid.map((g) => {
                const multi = g.buckets.length > 1;
                return (
                  <div key={g.productTypeId} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-color,#e4e8ee)' }}>
                    <div className="flex items-baseline justify-between gap-sm mb-sm">
                      <span className="font-semibold text-sm text-primary">{g.productName}</span>
                      <span className="ao-meta">{g.maxConcurrent != null ? t('availabilityCalendar.maxNote', { count: g.maxConcurrent }) : ''}</span>
                    </div>
                    {multi ? (
                      g.buckets.map((b) => (
                        <div key={b.size ?? 'none'}>
                          <div className="ao-sizelabel">{sizeName(b.size)}</div>
                          <div className="ao-chiprow">
                            {dayList.map((day) => {
                              const cell = b.cells[day];
                              if (!cell) return null;
                              const st = outlookCellState(cell);
                              const chip = (
                                <button key={day} className={`ao-chip st-${st}`} onClick={() => openDetail(cell)}>
                                  <span className="cd">{dow[ymdDow(day)][0]}{ymdDayNum(day)}</span>
                                  <span className="cn">{chipLabel(cell, st)}</span>
                                </button>
                              );
                              if (!monthStartDays.has(day)) return chip;
                              return <Fragment key={`${day}-w`}><span className="ao-monthsep">{monthShort(day)}</span>{chip}</Fragment>;
                            })}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="ao-chiprow">
                        {dayList.map((day) => {
                          const cell = aggCell(g, day);
                          if (!cell) return null;
                          const st = outlookCellState(cell);
                          const chip = (
                            <button key={day} className={`ao-chip st-${st}`} onClick={() => openDetail(cell)}>
                              <span className="cd">{dow[ymdDow(day)][0]}{ymdDayNum(day)}</span>
                              <span className="cn">{chipLabel(cell, st)}</span>
                            </button>
                          );
                          if (!monthStartDays.has(day)) return chip;
                          return <Fragment key={`${day}-w`}><span className="ao-monthsep">{monthShort(day)}</span>{chip}</Fragment>;
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </>
        )}
      </div>

      {/* Detail panel */}
      {selected && (() => {
        const st = outlookCellState(selected);
        return (
          <div className="card" data-testid="availability-outlook-detail">
            <div className="flex flex-wrap items-center justify-between gap-md mb-lg">
              <div>
                <h3 className="text-xl font-bold text-primary mb-xs">
                  {selected.productName}{selected.size ? ` · ${selected.size}` : ''}
                </h3>
                <p className="text-secondary text-sm">{dow[ymdDow(selected.day)]} {ymdDayNum(selected.day)} · {selected.day}</p>
              </div>
              <span className={`badge ${st === 'full' ? 'badge-danger' : st === 'tight' ? 'badge-warning' : st === 'available' ? 'badge-success' : 'badge-info'}`}>{stateLabel(st)}</span>
            </div>

            {st === 'closed' ? (
              <p className="text-secondary text-sm">{t('availabilityCalendar.closedInfo')}</p>
            ) : st === 'unlimited' ? (
              <p className="text-secondary text-sm">{t('availabilityCalendar.unlimitedInfo')}</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-lg mb-lg">
                  <div className="p-md bg-secondary rounded-lg">
                    <p className="text-2xl font-bold text-primary">{selected.capacity}</p>
                    <p className="text-sm text-secondary">{t('availabilityCalendar.capacity')}</p>
                  </div>
                  <div className="p-md bg-secondary rounded-lg">
                    <p className="text-2xl font-bold text-primary">{selected.booked}</p>
                    <p className="text-sm text-secondary">{t('availabilityCalendar.booked')}</p>
                  </div>
                  <div className="p-md bg-secondary rounded-lg">
                    <p className="text-2xl font-bold text-primary">{selected.blocked}</p>
                    <p className="text-sm text-secondary">{t('availabilityCalendar.blockedMaint')}</p>
                  </div>
                  <div className="p-md bg-success/10 rounded-lg">
                    <p className="text-2xl font-bold text-primary flex items-center gap-xs"><CheckCircle className="w-5 h-5 text-success" />{selected.free}</p>
                    <p className="text-sm text-secondary">{t('availabilityCalendar.freeToBook')}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-md">
                  <p className="text-sm text-secondary flex-1 min-w-[220px]">
                    {t('availabilityCalendar.engineCheck')}:{' '}
                    {engineFree === 'loading' ? <Loader2 className="inline w-4 h-4 animate-spin" /> : <b className="text-primary">{engineFree ?? '—'}</b>}
                  </p>
                  <button onClick={() => navigate('/bookings')} className="btn btn-secondary">{t('availabilityCalendar.viewBookings')}</button>
                  <button onClick={() => navigate('/inventory')} className="btn btn-primary">{t('availabilityCalendar.addInventory')}</button>
                </div>
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
}
